import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api/api';
import toast from 'react-hot-toast';
import SignaturePad from 'signature_pad';
import { ReactComponent as DigntecLogo } from '../../Assets/images/DigntecLogo.svg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './contractSign.css';

export default function ProviderSign() {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const sigPadRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [info, setInfo] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/provider-sign/verify/${token}`)
      .then(res => {
        setInfo(res.data);
        setSignerName(res.data.signerName || '');
        setLoading(false);
      })
      .catch(() => { setExpired(true); setLoading(false); });
  }, [token]);

  const initSigPad = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
    if (sigPadRef.current) sigPadRef.current.off();
    sigPadRef.current = new SignaturePad(canvas, {
      minWidth: 1.5, maxWidth: 3.5, penColor: '#1a1a2e',
      backgroundColor: 'rgba(0,0,0,0)', velocityFilterWeight: 0.7,
    });
  }, []);

  useEffect(() => {
    if (!loading && !expired && !submitted) {
      const timer = setTimeout(initSigPad, 100);
      window.addEventListener('resize', initSigPad);
      return () => { clearTimeout(timer); window.removeEventListener('resize', initSigPad); };
    }
  }, [loading, expired, submitted, initSigPad]);

  const handleSubmit = async () => {
    if (!signerName.trim()) return toast.error('Please enter your name');
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) return toast.error('Please sign above');

    setSubmitting(true);
    try {
      await api.post(`/provider-sign/submit/${token}`, {
        signerName: signerName.trim(),
        signatureImage: sigPadRef.current.toDataURL('image/png'),
      });
      setSubmitted(true);
      toast.success('Provider signature saved!');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to submit');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="cs-root"><div className="cs-loading">Loading...</div></div>;

  if (expired) return (
    <div className="cs-root">
      <div className="cs-card" style={{ textAlign: 'center', padding: 40 }}>
        <h2>Link Expired</h2>
        <p>This provider signing link has expired or is invalid.</p>
        <p>Please request a new link from the administrator.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="cs-root">
      <div className="cs-card" style={{ textAlign: 'center', padding: 40 }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
        <h2>Signature Saved!</h2>
        <p>Thank you, <strong>{signerName}</strong>.</p>
        <p>Your provider signature has been registered and will be automatically applied to all future contracts.</p>
        <p style={{ color: '#888', fontSize: 14, marginTop: 16 }}>You can now close this page.</p>
      </div>
    </div>
  );

  return (
    <div className="cs-root">
      <div className="cs-card">
        <div className="cs-header">
          <DigntecLogo style={{ width: 120, height: 'auto' }} />
          <h2>Provider Signature</h2>
        </div>

        <div className="cs-info">
          <p>This signature will be automatically embedded into the <strong>Provider section</strong> of all future contracts.</p>
          {info?.hasSignature && (
            <p style={{ color: '#d97706', fontSize: 13, marginTop: 8 }}>
              A provider signature already exists. Submitting a new one will replace it.
            </p>
          )}
        </div>

        <div className="cs-section">
          <h3>Your Name</h3>
          <input className="cs-name-input" value={signerName}
            onChange={e => setSignerName(e.target.value)}
            placeholder="Full Name" />
        </div>

        <div className="cs-section">
          <h3>Signature</h3>
          <div className="cs-canvas-wrap">
            <canvas ref={canvasRef} />
            <div className="cs-sig-actions">
              <button type="button" className="cs-action-btn" onClick={() => {
                if (!sigPadRef.current) return;
                const d = sigPadRef.current.toData();
                if (d.length > 0) { d.pop(); sigPadRef.current.fromData(d); }
              }}>Undo</button>
              <button type="button" className="cs-action-btn cs-action-clear"
                onClick={() => sigPadRef.current?.clear()}>Clear</button>
            </div>
          </div>
          <p className="cs-hint">Draw your signature above</p>
        </div>

        <button className="cs-submit" onClick={handleSubmit}
          disabled={submitting || !signerName.trim()}>
          {submitting ? 'Submitting...' : 'Save Provider Signature'}
        </button>

        <p className="cs-footer">
          Digntec LLC · 15 Veterans Way, Edgewater, NJ 07020 · support@digntec.com
        </p>
      </div>
    </div>
  );
}
